import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FileText, Sparkles, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, parseISO } from "date-fns";
import useSWR from "swr";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/features/pagination/layouts/pagination";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { visionService } from "@/services/vision-service";
import {
  ProposalListProvider,
  useProposalList,
  type ProposalFiltersState,
} from "./context/use-proposal-list";
import { ProposalFilters } from "./layouts/proposal-filters/proposal-filters";
import { ProposalAdvancedFilters } from "./layouts/proposal-filters/proposal-advanced-filters";
import { ProposalListTable } from "./layouts/list-table/list-table";
import { useBodyScrollLock } from "./utils/use-body-scroll-lock";

const FILTER_LABELS: Record<keyof ProposalFiltersState, string> = {
  search: "Busca",
  industry: "Segmento",
  city: "Cidade",
  contract_months: "Prazo",
  min_capacity: "Mín. posições",
  max_capacity: "Máx. posições",
  created_by_user_id: "Executivo",
  date_from: "De",
  date_to: "Até",
  has_license_fee: "License Fee",
  has_flexible_credits: "Créditos flexíveis",
};

function ProposalListPageContent() {
  const { data: executivesData } = useSWR(
    "proposal-executives",
    () => visionService.listExecutives(),
    { revalidateOnFocus: false },
  );
  const executives = executivesData?.executives ?? [];

  const {
    filters,
    error,
    refetch,
    total,
    totalPages,
    activePage,
    pageSize,
    setPage,
    updateFilters,
    clearFilters,
  } = useProposalList();

  const navigate = useNavigate();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  useBodyScrollLock();

  const periodChip =
    filters.date_from && filters.date_to ? (
      <span
        key="period"
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-card border text-xs font-medium"
      >
        {`Período: ${format(parseISO(filters.date_from), "dd/MM/yy")} – ${format(parseISO(filters.date_to), "dd/MM/yy")}`}
        <button
          type="button"
          onClick={() => updateFilters({ date_from: "", date_to: "" })}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    ) : null;

  const filterChips = (
    Object.entries(filters) as [keyof ProposalFiltersState, string | boolean][]
  )
    .filter(([key, value]) => {
      if (key === "date_from" || key === "date_to") return false;
      if (typeof value === "boolean") return value === true;
      return value !== "";
    })
    .map(([key, value]) => (
      <span
        key={key}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-card border text-xs font-medium"
      >
        {typeof value === "boolean"
          ? FILTER_LABELS[key]
          : key === "created_by_user_id"
            ? `${FILTER_LABELS[key]}: ${executives.find((e) => String(e.user_id) === value)?.full_name ?? value}`
            : `${FILTER_LABELS[key]}: ${value}`}
        <button
          type="button"
          onClick={() =>
            updateFilters({
              [key]: typeof value === "boolean" ? false : "",
            } as Partial<ProposalFiltersState>)
          }
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    ));

  const allChips = [...filterChips, ...(periodChip ? [periodChip] : [])];
  const hasChips = allChips.length > 0;

  return (
    <div className="min-h-screen bg-stone-100 px-8 py-6">
      <div className="mx-auto max-w-[90rem] space-y-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold tracking-tight">Propostas</h1>
          <div className="flex items-center gap-3">
            <Button
              size="lg"
              variant="outline"
              className="font-medium text-base px-6 py-6 shrink-0 rounded-xl"
              onClick={() => navigate(DEFAULT_ROUTES.ATLAS)}
            >
              Acessar Atlas
            </Button>
            <div className="relative">
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="lg"
                    className="bg-[var(--woba-yellow)] hover:bg-[var(--woba-yellow-hover)] text-black font-medium text-base px-6 py-6 shrink-0 shadow-none border-none rounded-xl"
                  >
                    Nova proposta
                    <ChevronDown
                      className={`ml-2 h-4 w-4 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="max-w-[260px] p-1.5 rounded-xl"
                >
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(
                        DEFAULT_ROUTES.VISION_360_AI_MODE,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer"
                  >
                    <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-yellow-500" />
                    <div>
                      <p className="text-base font-medium leading-tight">
                        Modo AI
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Recomendações a partir do deal no HubSpot
                      </p>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 opacity-50" />
                  <DropdownMenuItem
                    onClick={() =>
                      window.open(
                        DEFAULT_ROUTES.VISION_360,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    className="flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer"
                  >
                    <FileText className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-base font-medium leading-tight">
                        Modo Clássico
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        Recomendações a partir de dados manuais
                      </p>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 pointer-events-none">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-4 w-4 rounded-full bg-red-500 border-2 border-stone-100" />
              </span>
            </div>
          </div>
        </div>
        <ProposalFilters
          showAdvanced={showAdvanced}
          onToggleAdvanced={() => setShowAdvanced((prev) => !prev)}
        />
        <ProposalAdvancedFilters
          open={showAdvanced}
          onClose={() => setShowAdvanced(false)}
        />
        {hasChips && (
          <div className="flex flex-wrap items-center gap-2">
            {allChips}
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Limpar todos
            </button>
          </div>
        )}
        {error ? (
          <div className="rounded-lg border bg-card p-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Erro ao carregar propostas.
            </p>
            <Button variant="outline" onClick={refetch}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <ProposalListTable />
        )}
        {totalPages > 1 && (
          <Pagination
            count={total}
            page={activePage}
            onPageClick={setPage}
            onPrevPageClick={() => setPage(activePage - 1)}
            onNextPageClick={() => setPage(activePage + 1)}
            pageName="Propostas"
            totalLines={String(total)}
            totalPages={totalPages}
            containerClassName="bg-stone-100 border-t-0"
            leftContent={
              <span className="text-xs text-muted-foreground">
                Exibindo proposta {(activePage - 1) * pageSize + 1}–
                {Math.min(activePage * pageSize, total)} de {total}{" "}
                {total === 1 ? "proposta encontrada" : "propostas encontradas"}
              </span>
            }
          />
        )}
      </div>
    </div>
  );
}

export const ProposalListPage = () => (
  <ProposalListProvider>
    <ProposalListPageContent />
  </ProposalListProvider>
);
